/**
 * How a fill proposal is described to the user — shared by the grid, the review
 * popover and the page's per-tier bulk actions so the wording can never drift
 * between them.
 */

import type {
  CellFillTarget,
  DocxFieldFillTarget,
  FillProposal,
  FillSource,
} from '@/types/schedule'

/** Evidence is an open bag until Phase B settles it — read defensively. */
export function evidenceString(proposal: FillProposal, key: string): string | null {
  const value = proposal.evidence[key]
  return typeof value === 'string' && value.trim() ? value : null
}

export function evidenceNumber(proposal: FillProposal, key: string): number | null {
  const value = proposal.evidence[key]
  return typeof value === 'number' ? value : null
}

/** The document a cited fill came from, when the evidence names one. */
export function citedDocumentName(proposal: FillProposal): string | null {
  return (
    evidenceString(proposal, 'documentName') ??
    evidenceString(proposal, 'document_name') ??
    evidenceString(proposal, 'sourceDocumentLabel')
  )
}

/** Where a fill came from, in the user's words. */
export function fillSourceLabel(proposal: FillProposal): string {
  switch (proposal.source) {
    case 'profile':
      return 'From company profile'
    case 'evidence':
      return citedDocumentName(proposal) ?? 'From a tender document'
    case 'ai_drafted':
      return 'AI drafted'
    case 'user_provided':
      return 'You provided'
    case 'needs_input':
      return 'Needs your input'
  }
}

/** Short form used inside bulk copy — "Accept all 14 profile fills". */
export const FILL_SOURCE_TIER_LABEL: Record<FillSource, string> = {
  profile: 'profile',
  evidence: 'cited',
  ai_drafted: 'AI-drafted',
  user_provided: 'your',
  needs_input: 'unanswered',
}

/** The value that would land in the cell — a user edit wins over the proposal. */
export function effectiveFillValue(proposal: FillProposal): string {
  return proposal.userEditedValue ?? proposal.proposedValue
}

/**
 * True when accepting this proposal would write nothing.
 *
 * A `needs_input` fill is the AI saying it could not answer, and the fill run
 * raises those on number/date cells that often already hold a value the buyer
 * typed in. Accepting one unedited stages an empty write that BLANKS that cell —
 * so Accept stays closed until the user supplies a value. The backend enforces
 * the same rule; this is the affordance.
 */
export function needsUserValue(proposal: FillProposal): boolean {
  return proposal.source === 'needs_input' && effectiveFillValue(proposal).trim().length === 0
}

/** Why Accept is closed on an unanswered fill. */
export const NEEDS_VALUE_HINT = 'Add a value first — this fill needs your input.'

// ===================
// Target narrowing
// ===================

/**
 * The Excel cell a proposal writes, or null for any other target kind. Excel
 * surfaces read targets through this so a Word-form proposal can never be
 * painted into a grid.
 */
export function cellTargetOf(proposal: FillProposal): CellFillTarget | null {
  return proposal.target.kind === 'cell' ? proposal.target : null
}

/** The Word-form field a proposal writes, or null for any other target kind. */
export function docxTargetOf(proposal: FillProposal): DocxFieldFillTarget | null {
  return proposal.target.kind === 'docx_field' ? proposal.target : null
}

/** True when the proposal targets this Excel sheet. */
export function isOnSheet(proposal: FillProposal, sheet: string): boolean {
  return cellTargetOf(proposal)?.sheet === sheet
}

/** A short reference for where a proposal writes: `Sheet!B5`, or the field path. */
export function fillTargetLabel(proposal: FillProposal): string {
  const target = proposal.target
  return target.kind === 'cell' ? `${target.sheet}!${target.address}` : target.fieldId
}
